import { ReactNode, useState } from "react";
import { Box, BoxProps, LinkProps, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { Accordion, AccordionDetails, AccordionSummary, Chip, Fade, List, ListItem } from "@mui/material";
import { Variants } from "motion/react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { MotionLink, Span } from "src/components/base";
import { Button } from "src/components/base/button";
import { WfLinkUnstyled } from "src/components/base/wf-link";
import { TraderAvatar } from "src/components/common/trader-avatar";
import { IconCogWheel } from "src/components/icons";
import { IconCaret } from "src/components/icons-mui/caret";
import { FADE_TRANSITION_PROPS } from "src/components-mui/base/fade/fade-transition.constants";
import { HashLinkUnstyled } from "src/components-mui/base/hash-link/hash-link";
import { Typography } from "src/components-mui/base/typography/typography";
import { ID_NAV_MOBILE } from "src/constants/dom";
import { LOGOUT_URL } from "src/constants/urls";
import { useGetUserName } from "src/hooks/use-get-user-name";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { IUser } from "src/types/common";
import { INav, INavLink } from "src/types/layout";
import { createMiddleClickHandler } from "src/utils/dom/mouse/mouse";
import { getPathnameFromRouter } from "src/utils/url/url";
import { useNavigationTracking } from "./use-navigation-tracking";
import { useSearchViewModeLink } from "./use-search-view-mode-link";

interface INavLinkProps extends LinkProps {
    href: string;
    isActive: boolean;
    children: ReactNode | ReactNode[];
    target?: string;
    isChildLink?: boolean;
    shouldHaveBorder?: boolean;
}
// Dev-Note: should be wrapped in a NextLink when we want to use Next.js agnostic links
const NavLink = ({ isActive, href, isChildLink = false, shouldHaveBorder = true, children, ...props }: INavLinkProps) => {
    const linkProps = {
        className: "gtm-nav-menu__link",
        href,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        mx: isChildLink ? 0 : [2, 4],
        px: isChildLink ? [1, 2] : 0,
        fontWeight: isChildLink ? "normal" : "semibold",
        color: isActive ? "green.500" : undefined,
        _hover: { color: "green.500" },
        _active: { color: "green.500" },
        borderBottomColor: isChildLink || !shouldHaveBorder ? "" : "gray.100",
        borderBottomWidth: isChildLink || !shouldHaveBorder ? 0 : "1px",
    };
    return (
        <WfLinkUnstyled {...linkProps} {...props}>
            {children}
        </WfLinkUnstyled>
    );
};
interface IChildNavItemsProps {
    items: Required<INavLink>["children"];
    currentPath: string;
    onClick: () => void;
}
const ChildNavItems = ({ items, currentPath, onClick: onNavItemClick }: IChildNavItemsProps) => {
    const { t } = useTranslation("common");
    const { trackSubNavClick } = useNavigationTracking();
    const { handleSearchLinkClick } = useSearchViewModeLink();

    const onNavLinkClick = (linkUrl: string) => {
        trackSubNavClick(linkUrl);
        onNavItemClick();
    };

    return (
        <List role="menu" sx={{ p: 0 }}>
            <ListItem role="menuitem" disablePadding sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 1 }}>
                {items.map(({ link, isNew }, index) => (
                    <NavLink
                        key={`${index}${link}`}
                        href={link.url}
                        target={link.target}
                        isActive={link.url === currentPath}
                        h={[7, 8]}
                        pl={[2, 3]}
                        fontWeight="normal"
                        onClick={event => {
                            handleSearchLinkClick(event, link.url);
                            onNavLinkClick(link.url);
                        }}
                        onAuxClick={createMiddleClickHandler(() => onNavLinkClick(link.url))}
                        isChildLink
                    >
                        <Span fontSize={["md", "xl"]} noOfLines={1} wordBreak="break-all">
                            {link.name}
                        </Span>
                        {isNew && <Chip label={t("new")} size="medium" color="green" sx={{ ml: 1 }} />}
                    </NavLink>
                ))}
            </ListItem>
        </List>
    );
};

const userAreaVariants: Variants = {
    open: {
        y: 0,
        opacity: 1,
        transition: {
            bounce: 0,
            delay: 0.2,
            repeatType: "mirror",
        },
    },
    closed: {
        y: 50,
        opacity: 0,
        transition: {
            bounce: 0,
            repeatType: "mirror",
        },
    },
};

interface IUserAreaProps {
    user: IUser;
    userSettingUrl: string;
    isOpen: boolean;
    onClose: () => void;
}
// Dev-Note: should be wrapped in a NextLink when we want to use Next.js agnostic links
const UserArea = ({ user, userSettingUrl, isOpen, onClose }: IUserAreaProps) => {
    const getUserName = useGetUserName();
    return (
        <MotionLink
            href={userSettingUrl}
            flexShrink={0}
            px={[3, 5]}
            variants={userAreaVariants}
            initial="closed"
            animate={isOpen ? "open" : "closed"}
            onClick={onClose}
        >
            <Flex h={10} align="center">
                <TraderAvatar size="sm" mr={1} trader={user} flexShrink={0} />
                <Box flexGrow={1} overflow="hidden">
                    <Text fontWeight="semibold" noOfLines={1} wordBreak="break-all">
                        {getUserName(user)}
                    </Text>
                    <Text fontSize="sm" noOfLines={1} wordBreak="break-all">
                        {user.email}
                    </Text>
                </Box>
                <IconCogWheel flexShrink={0} boxSize={3} ml={1} />
            </Flex>
        </MotionLink>
    );
};

interface IProps extends BoxProps {
    nav: INav;
    user: IUser | null;
    isOpen: boolean;
    onClose: () => void;
}

export const MenuMobile = (props: IProps) => {
    const { nav, user, isOpen, onClose, ...otherProps } = props;
    const { t } = useTranslation("common");
    const { buildLoginModalLink, buildRegisterModalLink } = useLinkBuilder();
    const { trackMainNavClick } = useNavigationTracking();
    const { handleSearchLinkClick } = useSearchViewModeLink();
    const path = getPathnameFromRouter(useRouter());
    const [expandedItemIndex, setExpandedItemIndex] = useState<number | null>(null);

    const onAccordionItemChange = (url: string, index: number, isExpanded: boolean) => {
        if (isExpanded) {
            trackMainNavClick(url);
        }

        setExpandedItemIndex(isExpanded ? index : null);
    };

    const onNavLinkClick = (linkUrl: string) => {
        trackMainNavClick(linkUrl);
        onClose();
    };

    return (
        <Fade in={isOpen} mountOnEnter unmountOnExit onExited={() => setExpandedItemIndex(null)} {...FADE_TRANSITION_PROPS}>
            <Box
                as="nav"
                id={ID_NAV_MOBILE}
                pos="fixed"
                bottom="0"
                left="0"
                right="0"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                overflow="hidden"
                bg="white"
                pt={[3, 5]}
                zIndex="overlay"
                {...otherProps}
            >
                {user && nav.userSetting && <UserArea user={user} userSettingUrl={nav.userSetting.url} isOpen={isOpen} onClose={onClose} />}
                <Box flexGrow={1} overflow="auto">
                    {nav.main.map(({ link, children }, index) =>
                        children.length > 0 ? (
                            <Accordion
                                key={link.url}
                                expanded={expandedItemIndex === index}
                                onChange={(_, isExpanded) => onAccordionItemChange(link.url, index, isExpanded)}
                                slotProps={{ transition: { timeout: 200 } }}
                                sx={{
                                    width: "auto",
                                    mx: [3, 5],
                                    borderColor: "gray.100",
                                    borderBottomStyle: index + 1 === nav.main.length ? "none" : "solid",
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<IconCaret />}
                                    sx={theme => ({ p: 0, alignItems: "center", height: [theme.spacing(8), theme.spacing(10)], minHeight: 0 })}
                                >
                                    <Typography
                                        component="span"
                                        noWrap
                                        sx={{ fontSize: ["md", "xl"], fontWeight: "semibold", wordBreak: "break-all" }}
                                    >
                                        {link.name}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pb: [3, 2] }}>
                                    <ChildNavItems items={children} currentPath={path} onClick={onClose} />
                                </AccordionDetails>
                            </Accordion>
                        ) : (
                            <NavLink
                                key={link.url}
                                href={link.url}
                                target={link.target}
                                isActive={link.url === path}
                                onClick={event => {
                                    handleSearchLinkClick(event, link.url);
                                    onNavLinkClick(link.url);
                                }}
                                onAuxClick={createMiddleClickHandler(() => onNavLinkClick(link.url))}
                                px={0}
                                h={[8, 10]}
                                mx={[3, 5]}
                                borderBottomColor="gray.100"
                                borderBottomStyle={index + 1 == nav.main.length ? "none" : "solid"}
                                borderBottomWidth="1px"
                            >
                                <Span fontSize={["md", "xl"]} noOfLines={1} wordBreak="break-all">
                                    {link.name}
                                </Span>
                            </NavLink>
                        )
                    )}
                </Box>
                <Box flexShrink={0} mt={0.5} px={[3, 5]}>
                    <SimpleGrid columns={user ? 1 : 2} spacing={2} py={[3, 5]} borderTop="1px" borderColor="gray.100">
                        {user ? (
                            <Button as={WfLinkUnstyled} href={LOGOUT_URL} variant="outline">
                                {t("logout")}
                            </Button>
                        ) : (
                            <>
                                <Button className="gtm-nav-menu__login" as={HashLinkUnstyled} href={buildLoginModalLink()} variant="outline">
                                    {t("login-button")}
                                </Button>
                                <Button as={HashLinkUnstyled} href={buildRegisterModalLink()} colorScheme="green">
                                    {t("register")}
                                </Button>
                            </>
                        )}
                    </SimpleGrid>
                </Box>
            </Box>
        </Fade>
    );
};
